import type { PatientListItem, ProviderListItem, QueueOverview } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getJobQueues, searchPatients, searchProviders } from '@/config/api-helpers';
import { useDebounced } from '@/hooks/useDebounced';
import { recordVisit } from '@/hooks/useVisitHistory';
import { pages } from '@/router';

type CommandResultType = 'page' | 'subpage' | 'patient' | 'provider' | 'queue';

interface CommandResult {
  id: string;
  label: string;
  path: string;
  subtitle: string;
  type: CommandResultType;
}

const RESULT_TYPE_LABEL: Record<CommandResultType, string> = {
  page: 'Page',
  subpage: 'Subpage',
  patient: 'Patient',
  provider: 'Provider',
  queue: 'Queue',
};

const SUBPAGE_RESULTS = [
  {
    id: 'subpage:/devex-metrics?view=latency',
    label: 'Review Latency',
    path: '/devex-metrics?view=latency',
    subtitle: 'DevEx Metrics',
    type: 'subpage' as const,
  },
  {
    id: 'subpage:/devex-metrics?view=codex-feedback',
    label: 'Codex Feedback',
    path: '/devex-metrics?view=codex-feedback',
    subtitle: 'DevEx Metrics',
    type: 'subpage' as const,
  },
  {
    id: 'subpage:/deployments?environment=staging',
    label: 'Staging',
    path: '/deployments?environment=staging',
    subtitle: 'Deployments',
    type: 'subpage' as const,
  },
  {
    id: 'subpage:/deployments?environment=production',
    label: 'Production',
    path: '/deployments?environment=production',
    subtitle: 'Deployments',
    type: 'subpage' as const,
  },
  {
    id: 'subpage:/deployments?view=stats',
    label: 'Stats',
    path: '/deployments?view=stats',
    subtitle: 'Deployments',
    type: 'subpage' as const,
  },
];

export const COMMAND_MENU_OPEN_EVENT = 'dev-console:open-command-menu';

export function openCommandMenu() {
  window.dispatchEvent(new Event(COMMAND_MENU_OPEN_EVENT));
}

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function providerDisplayName(provider: ProviderListItem) {
  return `${provider.firstName} ${provider.lastName}`;
}

function pageMatchesQuery(page: { label: string; path: string }, query: string) {
  if (!query) return true;
  const haystack = `${page.label} ${page.path}`.toLowerCase();
  return haystack.includes(query);
}

function queueMatchesQuery(queue: QueueOverview, query: string) {
  if (!query) return false;
  const haystack = [
    queue.canonicalJob,
    queue.pod,
    ...queue.jobNames,
    `${queue.mainQueueCount} main messages`,
    `${queue.dlqCount} dlq messages`,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

export function CommandMenu() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debouncedQuery = useDebounced(query, 250);
  const normalizedQuery = normalize(debouncedQuery);
  const shouldSearchResources = normalizedQuery.length > 0;

  const { data: patientsData, isFetching: patientsFetching } = useQuery({
    queryKey: ['command-menu-patients-search', normalizedQuery],
    queryFn: () => searchPatients({ query: normalizedQuery, pageSize: 5 }),
    enabled: isOpen && shouldSearchResources,
  });

  const { data: providersData, isFetching: providersFetching } = useQuery({
    queryKey: ['command-menu-providers-search', normalizedQuery],
    queryFn: () => searchProviders({ query: normalizedQuery, pageSize: 5 }),
    enabled: isOpen && shouldSearchResources,
  });

  const { data: queuesData, isFetching: queuesFetching } = useQuery({
    queryKey: ['command-menu-job-queues'],
    queryFn: getJobQueues,
    enabled: isOpen && shouldSearchResources,
    staleTime: 30_000,
  });

  const results = useMemo<CommandResult[]>(() => {
    const pageResults = pages
      .filter((page) => pageMatchesQuery(page, normalizedQuery))
      .slice(0, normalizedQuery ? 6 : 8)
      .map((page) => ({
        id: `page:${page.path}`,
        label: page.label,
        path: page.path,
        subtitle: page.path === '/' ? 'Home' : page.path,
        type: 'page' as const,
      }));

    const subpageResults = SUBPAGE_RESULTS.filter((page) => pageMatchesQuery(page, normalizedQuery)).slice(
      0,
      normalizedQuery ? 4 : 6
    );

    const patientResults = (patientsData?.items ?? []).map((patient: PatientListItem) => ({
      id: `patient:${patient.patientId}`,
      label: patient.displayName,
      path: `/patients/${patient.patientId}`,
      subtitle: [
        `Patient ${patient.patientId}`,
        `User ${patient.userId}`,
        patient.healthieId ? `Healthie ${patient.healthieId}` : null,
      ]
        .filter(Boolean)
        .join(' / '),
      type: 'patient' as const,
    }));

    const providerResults = (providersData?.items ?? []).map((provider: ProviderListItem) => ({
      id: `provider:${provider.providerId}`,
      label: providerDisplayName(provider),
      path: `/providers/${provider.providerId}`,
      subtitle: `Provider ${provider.providerId} / User ${provider.userId} / ${provider.email}`,
      type: 'provider' as const,
    }));

    const queueResults = (queuesData ?? [])
      .filter((queue) => queueMatchesQuery(queue, normalizedQuery))
      .slice(0, 6)
      .map((queue) => ({
        id: `queue:${queue.canonicalJob}`,
        label: queue.canonicalJob,
        path: `/jobs?queue=${encodeURIComponent(queue.canonicalJob)}`,
        subtitle: `${queue.pod} / ${queue.jobNames.length} jobs / Main ${queue.mainQueueCount} / DLQ ${queue.dlqCount}`,
        type: 'queue' as const,
      }));

    return [...pageResults, ...subpageResults, ...queueResults, ...patientResults, ...providerResults];
  }, [normalizedQuery, patientsData?.items, providersData?.items, queuesData]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const selectResult = useCallback(
    (result: CommandResult) => {
      recordVisit(result.path, result.label, result.type);
      closeMenu();
      navigate(result.path);
    },
    [closeMenu, navigate]
  );

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const isCommandK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
      if (!isCommandK) return;

      event.preventDefault();
      setIsOpen((current) => !current);
    };

    window.addEventListener(COMMAND_MENU_OPEN_EVENT, handleOpen);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener(COMMAND_MENU_OPEN_EVENT, handleOpen);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (selectedIndex <= results.length - 1) return;
    setSelectedIndex(Math.max(results.length - 1, 0));
  }, [results.length, selectedIndex]);

  if (!isOpen) return null;

  const isSearching = shouldSearchResources && (patientsFetching || providersFetching || queuesFetching);

  return (
    <div
      className='fixed inset-0 z-[70] flex items-start justify-center bg-black/25 px-3 py-20'
      onMouseDown={closeMenu}
    >
      <div
        aria-label='Command menu'
        aria-modal='true'
        className='w-full max-w-2xl overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-2xl'
        onMouseDown={(event) => event.stopPropagation()}
        role='dialog'
      >
        <div className='border-b border-neutral-100 p-3'>
          <input
            ref={inputRef}
            aria-label='Search pages, subpages, queues, patients, and providers'
            className='w-full rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400 focus:outline-none'
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                closeMenu();
                return;
              }

              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedIndex((current) => Math.min(current + 1, Math.max(results.length - 1, 0)));
                return;
              }

              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedIndex((current) => Math.max(current - 1, 0));
                return;
              }

              if (event.key === 'Enter' && results[selectedIndex]) {
                event.preventDefault();
                selectResult(results[selectedIndex]);
              }
            }}
            placeholder='Search pages, subpages, queues, patients, providers...'
            value={query}
          />
        </div>

        <div className='max-h-[60vh] overflow-y-auto py-2'>
          {results.length === 0 && !isSearching && (
            <div className='px-4 py-8 text-center text-sm text-neutral-500'>No pages, subpages, or resources found</div>
          )}

          {results.map((result, index) => (
            <button
              key={result.id}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left ${
                index === selectedIndex ? 'bg-neutral-50' : 'hover:bg-neutral-25'
              }`}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => selectResult(result)}
              type='button'
            >
              <span className='w-16 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-400'>
                {RESULT_TYPE_LABEL[result.type]}
              </span>
              <span className='min-w-0 flex-1'>
                <span className='block truncate text-sm font-medium text-neutral-900'>{result.label}</span>
                <span className='block truncate text-xs text-neutral-500'>{result.subtitle}</span>
              </span>
            </button>
          ))}

          {results.length === 0 && isSearching && (
            <div className='px-4 py-3 text-sm text-neutral-500'>Searching resources...</div>
          )}
        </div>
      </div>
    </div>
  );
}
